import { MultiHeaderTableRow, MultiHeaderTableData } from "../../interfaces";
import * as React from "react";
import { Fragment } from "react";
import { TableCell, TableRow } from "@mui/material";
import { Theme } from "@mui/material/styles";
import makeStyles from '@mui/styles/makeStyles';
import clsx from "clsx";
import { formatDate } from "../../common/services/Utilities";
import RouterLink from "../RouterLink";

interface Props {
  tableData: MultiHeaderTableData;
  maximumNumberOfColumns: number;
  createTimestamp: string;
}

const useStyles = makeStyles((theme: Theme) => ({
  linkTd: {
    "padding": 0,
    "&:hover, &:focus": {
      background: "#d9e8f6",
    },
    "& > a:focus": {
      border: "none",
    },
    "&:active": {
      "background": "#97A5B9",
      "& a": {
        color: "#0A1935",
      },
    },
  },
  leftAlignHeader: {
    minHeight: 32,
    padding: 8,
    display: "flex",
    alignItems: "center",
  },
  leftAlignLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    minHeight: 32,
    padding: 8,
    display: "flex",
    alignItems: "center",
  },
  centerLink: {
    color: theme.palette.primary.main,
    textDecoration: "underline",
    minHeight: 32,
    padding: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  footer: {
    backgroundColor: theme.palette.primary.main,
  },
  footerText: {
    "backgroundColor": theme.palette.primary.main,
    "position": "relative",
    "& b": {
      position: "absolute",
      top: "50%",
      right: 15,
      whiteSpace: "nowrap",
      transform: "translateY(-50%)",
      color: theme.palette.common.white,
    },
  },
}));

export default function MultiHeaderTableBody({
  tableData,
  maximumNumberOfColumns,
  createTimestamp,
}: Props) {
  const classes = useStyles();
  const tableDataLength = Object.keys(tableData).length;
  return <>
    {Object.keys(tableData).map((key: string, index: number) => {
      // If there is a key with no data returned then return an empty fragment, otherwise display the data
      const headerId = key.toLowerCase().replace(/ /g, "-");
      return (
        <Fragment key={`${key}`}>
          <TableRow key={`${key}-header-row`} id={`${headerId}-header-row`}>
            <TableCell
              key={`${key}-header-cell`}
              id={`${headerId}-header-cell`}
              className={classes.leftAlignHeader}
            >
              <b>{key.toUpperCase()}</b>
            </TableCell>
            {[...Array(maximumNumberOfColumns - 1)].map(
              (value: number, index: number) => (
                <TableCell
                  id={`empty-${headerId}-header-cell-${index}`}
                  key={`empty-${key}-header-cell-${index}`}
                />
              ),
            )}
          </TableRow>
          {tableData[key].map((data: MultiHeaderTableRow) => {
            const id = (data.title.value as string)
              .toLowerCase()
              .replace(/[^a-zA-Z ]/g, "")
              .replace(/\s\s+/g, " ")
              .replace(/ /g, "-");
            return (
              <TableRow id={`${id}-row`} key={`${id}-row`}>
                {Object.keys(data).map((key: string) => (
                  <TableCell
                    style={data[key].styles}
                    key={`${id}-${key.toLowerCase()}-data-cell`}
                    id={`${id}-${key.toLowerCase()}-data-cell`}
                    className={data[key].onClick ? classes.linkTd : ""}
                  >
                    {data[key].link && data[key].onClick ? (
                      <RouterLink
                        buttonLink
                        key={`${id}-${key.toLowerCase()}-link`}
                        href={data[key].link as string}
                        onClick={data[key].onClick}
                        currentUrl={`/oscr${window.location.search}`}
                        className={
                          data[key].leftAlign
                            ? clsx(classes.leftAlignLink, "left-align-link")
                            : clsx(classes.centerLink, "center-link")
                        }
                      >
                        {data[key].value
                          ? data[key].value.toLocaleString()
                          : data[key].value}
                      </RouterLink>
                    ) : data[key].value ? (
                      data[key].value.toLocaleString()
                    ) : (
                      data[key].value
                    )}
                  </TableCell>
                ))}
              </TableRow>
            );
          })}
          {index !== tableDataLength ? (
            <TableRow id={`${index}-empty-row`} key={`${index}-empty-row`} />
          ) : (
            <></>
          )}
        </Fragment>
      );
    })}
    <TableRow>
      {[...Array(maximumNumberOfColumns - 1)].map(
        (value: number, index: number) => (
          <TableCell className={classes.footer} key={index} />
        ),
      )}
      <TableCell className={classes.footerText}>
        <b id="dateStamp">{`Data updated as of: ${formatDate(
          createTimestamp,
        )}`}</b>
      </TableCell>
    </TableRow>
  </>;
}
